<script lang="ts" setup>
import type Konva from "konva";
import type { KonvaEventObject, Node as KonvaNode } from "konva/lib/Node";
import { useMagicKeys } from "@vueuse/core";
import { nextTick } from "vue";

import { getKonvaComponents } from "@/telestrations/components/Konva.vue";
import { getEllipseUpdate } from "@/telestrations/helpers/getEllipseUpdate";
import { getRectangleUpdate } from "@/telestrations/helpers/getRectangleUpdate";
import { getShapeClickSize } from "@/telestrations/helpers/getShapeClickSize";
import { shapeUpdateStrategies } from "@/telestrations/helpers/shapeUpdateStrategies";
import { ShapeType } from "@/telestrations/types/canvas";
import { filterDuplicatePoints } from "~/telestrations/helpers/filterDuplicatePoints";
import { setupSpotLightShapeConfig } from "~/telestrations/helpers/spotlightShapeConfig";

import TShapeEllipse from "./shape/Ellipse.vue";

const konvaComponents = getKonvaComponents();
const toolsStore = useToolsStore();
const canvasStore = useCanvasStore();
const shapesStore = useShapesStore();
const telestrationStore = useTelestrationStore();
const transformerRef = ref<Konva.Transformer | null>(null);
const konvaStageRef = ref<Konva.Stage | null>(null);
const spotlightShapeConfig = ref({ ...setupSpotLightShapeConfig(canvasStore.getKonvaStage(), shapesStore.shapes) });
const { shift: shiftPressed, enter: enterPressed } = useMagicKeys();

// Add minimum drag distance threshold (in pixels)
const MIN_DRAG_DISTANCE = 5;
const initialClickPos = ref<{ x: number; y: number } | null>(null);
const lastPointerPos = ref<{ x: number; y: number } | null>(null);

onMounted(() => {
  canvasStore.konvaStageRef = konvaStageRef.value;
  shapesStore.transformerRef = transformerRef.value;
  spotlightShapeConfig.value = setupSpotLightShapeConfig(canvasStore.getKonvaStage(), shapesStore.shapes);

  // Setup keyboard event listener to prevent global keyboard shortcuts
  setupKeyboardListener();
});

onUnmounted(() => {
  const stage = canvasStore.getKonvaStage();
  if (!stage) return;
  const container = stage.container();
  container.removeEventListener("keydown", handleKeyDown, { capture: true });
});

function handleKeyDown(e: KeyboardEvent): void {
  // Only intercept when a shape is selected to prevent interference with other components
  const { selectedShape } = shapesStore;
  if (!selectedShape) return;

  // Prevent default and stop propagation for arrow keys when shape is selected
  if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
    e.preventDefault();
    e.stopPropagation();

    // Handle the arrow key movement for selected shape
    const x = selectedShape.x ?? 0;
    const y = selectedShape.y ?? 0;
    const distance = 1;
    switch (e.key) {
      case "ArrowLeft":
        shapesStore.updateShapePosition(selectedShape, x - distance, y);
        break;
      case "ArrowRight":
        shapesStore.updateShapePosition(selectedShape, x + distance, y);
        break;
      case "ArrowUp":
        shapesStore.updateShapePosition(selectedShape, x, y - distance);
        break;
      case "ArrowDown":
        shapesStore.updateShapePosition(selectedShape, x, y + distance);
        break;
    }
  }

  if (["Delete", "Backspace"].includes(e.key)) {
    e.preventDefault();
    e.stopPropagation();
    shapesStore.removeShape(selectedShape.id);
  }
}

function setupKeyboardListener(): void {
  const stage = canvasStore.getKonvaStage();
  if (!stage) return;

  // Make stage focusable to receive keyboard events
  const container = stage.container();
  container.tabIndex = 1;
  // Add keyboard event listener to the stage container

  // Add event listener with capture: true to intercept before other handlers
  container.addEventListener("keydown", handleKeyDown, { capture: true });
}

async function handleStageMouseDown(e: Event) {
  if (telestrationStore.telestrationMode === TelestrationMode.PREVIEW) {
    return;
  }
  const node = e.target as KonvaNode | null;

  if (!node) {
    console.warn("No node found during stage mousedown");
    return;
  }

  const stage = node.getStage();

  if (!stage) {
    console.warn("No Konva stage found");
    return;
  }
  const pos = stage.getPointerPosition();

  if (!pos) {
    console.warn("No pointer position found");
    return;
  }

  // If an arrow is being drawn (after the first drag), a click adds a new fixed point.
  if (shapesStore.drawingShape?.type === ShapeType.Arrow && !shapesStore.isDrawing) {
    const points = shapesStore.drawingShape.points || [];
    // The last point is the preview point, we fix it and add a new preview point.
    const lastX = points[points.length - 2];
    const lastY = points[points.length - 1];
    const newPoints = [...points, lastX, lastY];
    shapesStore.updateShape(shapesStore.drawingShape, { points: newPoints });
    return;
  }

  const clickedOnEmpty = node === stage;

  // clicked on stage - clear selection
  if (clickedOnEmpty) {
    if (toolsStore.selectedTool === ShapeType.Text && !shapesStore.drawingShape) {
      shapesStore.handleSelectedShapeDraw(e);
      await nextTick();
    }

    // For arrow tool, immediately create shape and start cursor-following mode
    if (toolsStore.selectedTool === ShapeType.Arrow) {
      const scaleX = stage.scaleX() ?? 1;
      const scaleY = stage.scaleY() ?? 1;
      const offsetX = stage.offsetX() ?? 0 / scaleX;
      const offsetY = stage.offsetY() ?? 0 / scaleY;
      const scaledX = pos.x / scaleX + offsetX;
      const scaledY = pos.y / scaleY + offsetY;

      shapesStore.handleSelectedShapeDraw(e);
      await nextTick();

      // Add initial points for arrow (start point + preview point)
      if (shapesStore.drawingShape) {
        shapesStore.updateShape(shapesStore.drawingShape, {
          points: [scaledX, scaledY, scaledX, scaledY],
        });
        shapesStore.isDrawing = false; // Stop drag mode, start cursor-following
      }
      return;
    }

    // Store initial click position for other shapes
    initialClickPos.value = { x: pos.x, y: pos.y };
    shapesStore.isDrawing = true;
    return;
  }

  // clicked on transformer - do nothing
  const clickedOnTransformer = node.getParent()?.className === "Transformer";

  if (clickedOnTransformer) {
    return;
  }

  const shape = shapesStore.shapes.find((s) => s.id === node.id());
  shapesStore.selectShape(shape ?? null);
}

async function handleStageMouseUp(e?: KonvaEventObject<MouseEvent>) {
  if (telestrationStore.telestrationMode === TelestrationMode.PREVIEW) {
    return;
  }

  lastPointerPos.value = null;

  const node = e?.target as KonvaNode | null;
  if (!node) {
    console.warn("No node found during stage mouseup");
    return;
  }
  const stage = node.getStage();
  if (!stage) {
    console.warn("No Konva stage found");
    return;
  }

  const pos = stage.getPointerPosition();

  if (initialClickPos.value && pos) {
    const dragDistance = Math.sqrt((pos.x - initialClickPos.value.x) ** 2 + (pos.y - initialClickPos.value.y) ** 2);

    if (dragDistance < MIN_DRAG_DISTANCE) {
      const type = toolsStore.selectedTool as ShapeType;
      const size = getShapeClickSize(stage, type);
      if (size) {
        shapesStore.addShapeOnClick(size);
        initialClickPos.value = null;
        return;
      }
    }
  }

  // For all shapes, finish the drawing.
  if (shapesStore.isDrawing) {
    shapesStore.isDrawing = false;
    initialClickPos.value = null;

    if (shapesStore.drawingShape) {
      shapesStore.selectShape(shapesStore.drawingShape);
      shapesStore.drawingShape = null;
      toolsStore.resetToSelectionTool();
    }
  }
}

async function handleStageMouseMove(e: Konva.KonvaEventObject<MouseEvent>) {
  const { telestrationMode } = telestrationStore;

  if (telestrationMode === TelestrationMode.PREVIEW) {
    return;
  }

  e.evt.preventDefault();

  const stage = e.target.getStage();
  if (!stage) {
    console.warn("Stage is null");
    return;
  }

  const pos = stage.getPointerPosition();
  if (!pos) {
    console.warn("Pointer position or initial click position is null");
    return;
  }

  lastPointerPos.value = { x: pos.x, y: pos.y };

  const scaleX = stage.scaleX() ?? 1;
  const scaleY = stage.scaleY() ?? 1;
  const scaledX = pos.x / scaleX;
  const scaledY = pos.y / scaleY;
  const offsetX = stage.offsetX() ?? 0 / scaleX;
  const offsetY = stage.offsetY() ?? 0 / scaleY;

  // Arrow: update only the last segment (preview)
  if (shapesStore.drawingShape?.type === ShapeType.Arrow) {
    const points = shapesStore.drawingShape.points || [];
    if (points.length > 0) {
      const newPoints = [...points];
      newPoints[newPoints.length - 2] = scaledX + offsetX;
      newPoints[newPoints.length - 1] = scaledY + offsetY;
      shapesStore.updateShape(shapesStore.drawingShape, { points: newPoints });
    }
    return;
  }

  // This part is for drag-to-draw other shapes and the first arrow segment
  if (!shapesStore.isDrawing) {
    return;
  }

  if (!shapesStore.drawingShape) {
    if (!initialClickPos.value) {
      return;
    }
    // Calculate drag distance
    const dragDistance = Math.sqrt((pos.x - initialClickPos.value.x) ** 2 + (pos.y - initialClickPos.value.y) ** 2);

    // Only create shape if dragged beyond minimum distance (skip for arrow tool)
    if (dragDistance >= MIN_DRAG_DISTANCE && !shapesStore.drawingShape && toolsStore.selectedTool !== ShapeType.Arrow) {
      shapesStore.handleSelectedShapeDraw(e as unknown as Event);
      await nextTick();
    }
  }

  // If we haven't started drawing yet, don't proceed
  if (!shapesStore.drawingShape) {
    return;
  }

  const { startX, startY, type } = shapesStore.drawingShape;

  let shapeUpdate: Partial<typeof shapesStore.drawingShape> = {};

  switch (type) {
    case ShapeType.Rectangle: {
      shapeUpdate = getRectangleUpdate({
        shape: shapesStore.drawingShape,
        pointer: { x: scaledX, y: scaledY },
        isSquare: shiftPressed.value,
        offset: { x: offsetX, y: offsetY },
      });
      break;
    }
    case ShapeType.Spotlight: {
      if (shapesStore.drawingShape.name === SpotlightShapeName.ELLIPSE) {
        shapeUpdate = getEllipseUpdate({
          shape: shapesStore.drawingShape,
          pointer: { x: scaledX, y: scaledY },
          isCircle: shiftPressed.value,
          offset: { x: offsetX, y: offsetY },
        });
      } else {
        const width = scaledX - startX;
        const height = scaledY - startY;
        shapeUpdate = {
          width: Math.abs(width),
          height: Math.abs(height),
          x: width < 0 ? startX + width + offsetX : startX + offsetX,
          y: height < 0 ? startY + height + offsetY : startY + offsetY,
        };
      }
      break;
    }
    case ShapeType.Ellipse: {
      shapeUpdate = getEllipseUpdate({
        shape: shapesStore.drawingShape,
        pointer: { x: scaledX, y: scaledY },
        isCircle: shiftPressed.value,
        offset: { x: offsetX, y: offsetY },
      });
      break;
    }
    case ShapeType.Line: {
      const node = shapesStore.getShapeNode(shapesStore.drawingShape) as Konva.Line | null;

      if (!node) {
        console.warn("No node found");
        return;
      }
      shapeUpdate = {
        points: [...node.points(), scaledX + offsetX, scaledY + offsetY],
      };
      break;
    }
    default:
      console.warn(`Shape type "${type}" not handled`);
      return;
  }
  shapesStore.updateShape(shapesStore.drawingShape, shapeUpdate);
  await nextTick();
  shapesStore.updateTransformerPosition();
}

function onTransformEvent(e: KonvaEventObject<Event>) {
  const attrs = e.target.attrs as {
    type: ShapeType;
    x: number;
    y: number;
    width: number;
    height: number;
    scaleX: number;
    scaleY: number;
    rotation: number;
    id: string;
  };

  if (e.type === "transformstart" || e.type === "dragstart") {
    shapesStore.startTransforming();
  } else if (e.type === "transformend" || e.type === "dragend") {
    shapesStore.stopTransforming();
  }

  shapesStore.updateTransformerPosition();
  if (attrs.type === ShapeType.Spotlight || (e.type === "dragmove" && shapesStore.selectedShape?.type === ShapeType.Spotlight)) {
    spotlightShapeConfig.value = setupSpotLightShapeConfig(canvasStore.getKonvaStage(), shapesStore.shapes, attrs);
  }
}

watch(
  () => shapesStore.shapes,
  () => {
    spotlightShapeConfig.value = setupSpotLightShapeConfig(canvasStore.getKonvaStage(), shapesStore.shapes);
  },
  { deep: true }
);

// Reactively update ellipse/circle on Shift key change, even if mouse is not moving
watch(shiftPressed, () => {
  if (shapesStore.isDrawing && shapesStore.drawingShape && lastPointerPos.value) {
    const stage = canvasStore.getKonvaStage();
    if (!stage) return;
    const scaleX = stage.scaleX() ?? 1;
    const scaleY = stage.scaleY() ?? 1;
    const type = shapesStore.drawingShape.type;

    const updater = shapeUpdateStrategies[type];
    if (!updater) {
      return;
    }

    const update = updater({
      shape: shapesStore.drawingShape,
      pointer: {
        x: lastPointerPos.value.x / scaleX,
        y: lastPointerPos.value.y / scaleY,
      },
      shift: shiftPressed.value,
      offset: {
        x: stage.offsetX() ?? 0 / scaleX,
        y: stage.offsetY() ?? 0 / scaleY,
      },
    });
    shapesStore.updateShape(shapesStore.drawingShape, update);
    shapesStore.updateTransformerPosition();
  }
});

watch(enterPressed, () => {
  if (enterPressed.value) {
    if (shapesStore.drawingShape?.type === ShapeType.Arrow) {
      shapesStore.selectShape(shapesStore.drawingShape);
      shapesStore.drawingShape = null;
      shapesStore.isDrawing = false;
      toolsStore.resetToSelectionTool();
    }
  }
});

function handleStageDoubleClick() {
  if (shapesStore.drawingShape?.type === ShapeType.Arrow) {
    const points = filterDuplicatePoints(shapesStore.drawingShape.points || []);
    shapesStore.updateShape(shapesStore.drawingShape, { points });

    shapesStore.selectShape(shapesStore.drawingShape);
    shapesStore.drawingShape = null;
    shapesStore.isDrawing = false;
    toolsStore.resetToSelectionTool();
  }
}

function handleStageClick(e: Event) {
  if (telestrationStore.telestrationMode === TelestrationMode.PREVIEW) {
    return;
  }

  const node = e.target as KonvaNode | null;

  if (!node) {
    return;
  }

  const stage = node.getStage();

  if (!stage) {
    return;
  }

  const clickedOnEmpty = node === stage;

  // clicked on stage - hide options only if Select tool is already selected
  if (clickedOnEmpty && toolsStore.selectedTool === "selection") {
    // This will deselect any currently selected shapes
    shapesStore.selectShape(null);
  }
}

const handleShapeMouseOver = (e: any) => {
  const node = e.target as KonvaNode | null;
  const stage = node?.getStage();

  if (!stage) return;

  const hoveringOnEmpty = node === stage;

  if (!hoveringOnEmpty && !shapesStore.isDrawing && toolsStore.selectedTool !== ShapeType.Arrow) {
    stage.container().style.cursor = "move";
  } else if (hoveringOnEmpty) {
    stage.container().style.cursor = toolsStore.isSelectionMode ? "default" : "crosshair";
  }
};
</script>

<template>
  <div class="min-h-196 relative z-0 w-full">
    <div
      class="inline-block align-bottom"
      :class="{
        'cursor-crosshair': !toolsStore.isSelectionMode,
      }"
    >
      <component
        :is="konvaComponents['vStage']"
        ref="konvaStageRef"
        :config="canvasStore.stageConfig"
        @mousedown="handleStageMouseDown"
        @touchstart="handleStageMouseDown"
        @mousemove="handleStageMouseMove"
        @touchmove="handleStageMouseMove"
        @mouseup="handleStageMouseUp"
        @touchend="handleStageMouseUp"
        @dblclick="handleStageDoubleClick"
        @click="handleStageClick"
        @mouseover="handleShapeMouseOver"
      >
        <component
          :is="konvaComponents['vLayer']"
        >
          <component
            :is="konvaComponents['vShape']"
            :config="spotlightShapeConfig"
          />
          <template v-for="shape in shapesStore.shapes" :key="shape.id">
            <TShapeEllipse v-if="shape.type === ShapeType.Ellipse" :shape="shape" />
            <TShapeArrow v-if="shape.type === ShapeType.Arrow" :shape="shape" />
            <TShapeLine v-if="shape.type === ShapeType.Line" :shape="shape" />
            <TShapeRect v-if="shape.type === ShapeType.Rectangle" :shape="shape" />
            <TShapeText v-if="shape.type === ShapeType.Text" :shape="shape" />
            <TShapeSpotlight v-if="shape.type === ShapeType.Spotlight" :shape="shape" />
          </template>
          <component
            :is="konvaComponents['vTransformer']"
            ref="transformerRef"
            :config="shapesStore.transformerConfig"
            @dragstart="onTransformEvent"
            @dragmove="onTransformEvent"
            @dragend="onTransformEvent"
            @click="onTransformEvent"
            @transformstart="onTransformEvent"
            @transform="onTransformEvent"
            @transformend="onTransformEvent"
          />
        </component>
      </component>
    </div>
  </div>
</template>
